import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, forkJoin, map, of } from 'rxjs';
import { CartItem } from '../../models/cart-item.model';
import { RequestFormData } from '../../models/request-form.model';
import { Calendario, Cliente, Comunicazione, FindLimitiClienteDto, Fornitura, FornituraClienteDto, Indirizzo, InfoRitiro, LimiteClienteDto, ObjectId, PagedResponse, Prenotazione, PrenotazioneFrontofficeResourceService, PrenotazioneUtenteIndirizzoDto, PrenotazioneViewDTO, Prodotto, ServiziComunaliResponse, ServizioComunaleDto, SlotDto, StatoPagamentiEnum, Totale } from '../../app/api/prenotazione';
import { IndirizzoRicercaDto, StradarioDTO } from '../../app/api/indirizzo';
import { CatalogoRicerca, RicercaCatalogoFrontofficeResourceService } from '../../app/api/catalogo';
import { LoadingService } from './loading.service';

const STORAGE_KEY = 'requestFormState';

// I DTO di visualizzazione restituiscono i valori enum come testo leggibile (es. "Non regolare",
// non "NON_REGOLARE"): normalizziamo prima di usarli come StatoPagamentiEnum
const normalizeEnumValue = (value: string): string => value.toUpperCase().replace(/\s+/g, '_');

interface RequestFormState {
  currentStep: number;
  skipUserStep: boolean;
  data: RequestFormData;
}

@Injectable({ providedIn: 'root' })
export class RequestFormService {
  private readonly prenotazioneFOService = inject(PrenotazioneFrontofficeResourceService);
  private readonly catalogoFOService = inject(RicercaCatalogoFrontofficeResourceService);
  private readonly loadingService = inject(LoadingService);
  private readonly router = inject(Router);

  // Campi step 1 (utenza)
  readonly utenza = signal<FornituraClienteDto | null>(null);

  // Campi step 2 (fornitura e servizio)
  readonly fornitura = signal<Fornitura | null>(null);
  readonly service = signal<ServizioComunaleDto | null>(null);
  readonly comune = signal<string | undefined>(undefined);
  readonly sogliaMassima = signal<number | null>(null);
  readonly servizioMaster = signal<string | null>(null);
  readonly gestioneServizioComunale = signal<string | null>(null);
  readonly exposureRulesText = signal<string | null>(null);

  // Campi step 3 (carrello)
  readonly cartItems = signal<CartItem[]>([]);

  // Volume totale del carrello in metri cubi (quantità x metriCubi di ogni articolo)
  readonly cartVolume = computed(() => {
    const total = this.cartItems().reduce((sum, cartItem) => sum + (cartItem.item.metriCubi ?? 0) * cartItem.quantity, 0);
    return Math.round(total * 100) / 100;
  });

  // Riempimento del carrello in percentuale rispetto alla soglia, arrotondato a 2 decimali
  readonly cartProgress = computed(() => {
    const soglia = this.sogliaMassima();
    if (!soglia) {
      return 0;
    }
    return Math.round((this.cartVolume() / soglia) * 100 * 100) / 100;
  });

  // Campi step 4 (dettagli)
  readonly exposureAddress = signal<string | null>(null);
  // Presente solo se l'indirizzo è stato scelto dalla tendina suggerimenti, non se digitato a mano
  readonly exposureAddressDetails = signal<IndirizzoRicercaDto | null>(null);
  readonly exposureAddressGeoDetails = signal<StradarioDTO | null>(null);

  readonly selectedSlot = signal<SlotDto | null>(null);
  readonly firstDate = signal<string | null>(null);
  readonly pickupDate = computed(() => this.selectedSlot()?.data ?? null);
  readonly pickupTimeSlot = computed(() => {
    const slot = this.selectedSlot();
    if (!slot) {
      return '';
    }
    const from = (slot.turnoOreDa ?? '').slice(0, 5);
    const to = (slot.turnoOreA ?? '').slice(0, 5);
    return `${from} - ${to}`;
  });

  readonly notes = signal<string>('');
  readonly phone = signal<string>('');
  readonly email = signal<string>('');

  readonly detailFormValid = signal<boolean>(false);

  // Generici
  readonly totalSteps = 5;
  currentStep = signal<number>(1);

  // true quando l'utente ha un solo codice cliente e lo step 1 (utenza) viene saltato
  readonly skipUserStep = signal<boolean>(false);
  // Messaggio da mostrare se il passaggio allo step successivo fallisce (es. errore API bloccante)
  readonly transitionError = signal<string | null>(null);
  // Risposta dell'ultima prenotazione creata/modificata con successo, letta dalla pagina di conferma
  readonly submittedPrenotazione = signal<Prenotazione | null>(null);


  readonly editingId = signal<string | null>(null);
  readonly editingCodPrenotazione = signal<string | null>(null);

  constructor() {
    this.restore();
  }

  // ==================== Navigazione tra step ====================

  canGoNext = computed(() => {
    if (this.currentStep() === 1) {
      return this.utenza() !== null;
    }
    if (this.currentStep() === 2) {
      return this.fornitura() !== null && this.service() !== null;
    }
    if (this.currentStep() === 3) {
      return this.cartItems() !== null && this.cartItems().length > 0 && this.cartProgress() <= 100;
    }
    if (this.currentStep() === 4) {
      return this.detailFormValid();
    }
    if (this.currentStep() === this.totalSteps) {
      return true;
    }
    return this.currentStep() < this.totalSteps;
  });

  readonly showStepNav = computed(() =>
    !(this.currentStep() === 2 && this.fornitura() === null)
  );

  goToStep(n: number): void {
    if (n >= 1 && n <= this.totalSteps) {
      this.transitionError.set(null);
      this.currentStep.set(n);
      this.persist();
    }
  }

  nextStep(): void {
    if (!this.canGoNext()) {
      return;
    }

    this.transitionError.set(null);

    if (this.currentStep() === this.totalSteps) {
      this.submitPrenotazione();
      return;
    }

    if (this.currentStep() === 2) {
      this.fetchSogliaMassimaAndAdvance();
      return;
    }

    this.advanceStep();
  }

  private advanceStep(): void {
    this.currentStep.update(s => s + 1);
    this.persist();
  }

  goBack(): void {
    this.transitionError.set(null);

    if (this.editingId() && this.currentStep() === 3) {
      this.exitWizard();
      return;
    }

    // Step 2 con fornitura già scelta: "indietro" la deseleziona per tornare alla ricerca indirizzo
    if (this.currentStep() === 2 && this.fornitura() !== null) {
      this.fornitura.set(null);
      this.comune.set(undefined);
      this.service.set(null);
      this.sogliaMassima.set(null);
      this.servizioMaster.set(null);
      this.gestioneServizioComunale.set(null);
      this.exposureRulesText.set(null);
      this.exposureAddress.set(null);
      this.exposureAddressDetails.set(null);
      this.exposureAddressGeoDetails.set(null);
      this.selectedSlot.set(null);
      this.firstDate.set(null);
      this.persist();
      return;
    }

    // Se lo step 1 è stato saltato, lo step 2 è di fatto il primo step di questa sessione:
    // "indietro" senza fornitura scelta deve uscire dal wizard, non tornare a uno step 1 mai visto
    if (this.currentStep() === 2 && this.skipUserStep()) {
      this.exitWizard();
      return;
    }

    if (this.currentStep() === 1) {
      this.exitWizard();
      return;
    }

    if (this.currentStep() > 1) {
      this.currentStep.update(s => s - 1);
      this.persist();
    }
  }

  private exitWizard(): void {
    const match = this.router.url.match(/\/modifica-richiesta\/([^/?#]+)/);
    if (match) {
      this.router.navigate(['/dettaglio-richiesta', match[1]]);
    } else {
      this.router.navigate(['/home']);
    }
  }

  // ==================== Step 2: selezione servizio ====================

  selectService(service: ServizioComunaleDto): void {
    // svuotiamo il carrello quando selezionato nuovo servizio
    if (this.service()?.id !== service.id) {
      this.cartItems.set([]);
    }
    this.service.set(service);
    this.sogliaMassima.set(null);
    this.servizioMaster.set(null);
    this.gestioneServizioComunale.set(null);
    this.exposureRulesText.set(null);
    this.transitionError.set(null);
  }

  private fetchSogliaMassimaAndAdvance(): void {
    const service = this.service();
    if (!service?.id) {
      this.advanceStep();
      return;
    }

    const fornitura = this.fornitura();
    const limitiRequest: FindLimitiClienteDto = {
      codCliente: fornitura?.codiceCliente,
      codContratto: fornitura?.contratto,
      idServizioComunale: service.id,
      utenteFragile: fornitura?.utenzaFragile ?? false
    };

    this.loadingService.show();
    forkJoin({
      servizioComunale: this.prenotazioneFOService.prenotazioneFoViewServizioComunaleIdGet(service.id),
      limiti: this.prenotazioneFOService.prenotazioneFoViewUserLimitiPrenotazioniPost(limitiRequest).pipe(
        map((response: LimiteClienteDto | LimiteClienteDto[] | PagedResponse) => {
          const limiti = (response as PagedResponse)?.results ?? response;
          return (Array.isArray(limiti) ? limiti : [limiti]).filter(Boolean) as LimiteClienteDto[];
        }),
        catchError((error) => {
          console.error('Error fetching limiti prenotazioni:', error);
          return of([] as LimiteClienteDto[]);
        })
      )
    }).pipe(
      finalize(() => this.loadingService.hide())
    ).subscribe({
      next: ({ servizioComunale, limiti }) => {
        const limite = limiti[0];
        if (limite?.limitePrenotazione != null && limite?.ordiniTotali != null && limite.ordiniTotali >= limite.limitePrenotazione) {
          this.transitionError.set('requestForm.steps.service.limitReached');
          return;
        }

        this.sogliaMassima.set(servizioComunale?.tipoServizioComunale?.servizioGratuito?.sogliaMassima ?? null);
        this.gestioneServizioComunale.set(servizioComunale?.gestioneServizio ?? null);
        this.servizioMaster.set(servizioComunale?.servizioMaster?.descrizione ?? null);
        this.exposureRulesText.set(servizioComunale?.note?.messaggioEsposizione ?? null);

        this.advanceStep();
      },
      error: (error) => {
        console.error('Error fetching servizio comunale detail:', error);
        this.transitionError.set('requestForm.steps.service.serviceDetailError');
      }
    });
  }

  // ==================== Modifica prenotazione ====================
  loadForEditing(id: string): void {
    this.loadingService.show();
    this.prenotazioneFOService.prenotazioneFoViewPrenotazioneIdGet(id).pipe(
      finalize(() => this.loadingService.hide())
    ).subscribe({
      next: (prenotazione: PrenotazioneViewDTO) => this.applyExistingPrenotazione(id, prenotazione),
      error: (error) => console.error('Error loading prenotazione for editing:', error)
    });
  }

  private applyExistingPrenotazione(id: string, prenotazione: PrenotazioneViewDTO): void {
    const cliente = prenotazione.cliente;
    const indirizzo = prenotazione.indirizzo;
    const calendario = prenotazione.calendario;

    this.editingId.set(id);
    this.editingCodPrenotazione.set(prenotazione.codPrenotazione ?? null);

    this.utenza.set({
      nomeCognome: [cliente?.nome, cliente?.cognome].filter(Boolean).join(' ') || undefined,
      contratto: cliente?.contratto,
      codiceCliente: cliente?.codiceCliente
    });

    this.fornitura.set({
      nome: cliente?.nome,
      cognome: cliente?.cognome,
      codiceCliente: cliente?.codiceCliente,
      contratto: cliente?.contratto,
      fornitura: cliente?.fornitura,
      idViario: cliente?.idViario ?? indirizzo?.idViario,
      indirizzo: cliente?.indirizzoFornitura ?? indirizzo?.indirizzo,
      codIstat: indirizzo?.codIstat,
      cellulare: cliente?.cellulare,
      email: cliente?.email,
      codiceFiscale: cliente?.codiceFiscale,
      partitaIva: cliente?.partitaIVA,
      utenzaFragile: cliente?.utenzaFragile,
      pagamenti: cliente?.statoPagamenti ? (normalizeEnumValue(cliente.statoPagamenti) as StatoPagamentiEnum) : undefined,
      raggrStruttReg: indirizzo?.raggrStruttReg,
      latitudine: indirizzo?.latitudine,
      longitudine: indirizzo?.longitudine,
      cap: indirizzo?.cap,
      testoContratto: cliente?.testoContratto,
      validitaDa: cliente?.validitaDa,
      validitaA: cliente?.validitaA,
      classe: cliente?.classe,
      contrattoPadre: cliente?.contrattoPadre ?? false
    });

    this.comune.set(indirizzo?.comune);
    this.gestioneServizioComunale.set(prenotazione.gestioneServizioComunale ?? null);
    this.servizioMaster.set(prenotazione.servizioMaster ?? null);

    this.exposureAddress.set(indirizzo?.indirizzoCompleto ?? indirizzo?.indirizzo ?? null);
    this.exposureAddressDetails.set(indirizzo ? {
      idViario: indirizzo.idViario,
      via: indirizzo.via,
      indirizzo: indirizzo.indirizzo,
      civico: indirizzo.civico,
      barrato: indirizzo.barrato,
      comune: indirizzo.comune,
      indirizzoCompleto: indirizzo.indirizzoCompleto,
      localita: indirizzo.localita,
      codIstat: indirizzo.codIstat
    } : null);
    this.exposureAddressGeoDetails.set(indirizzo ? {
      indirizzo: { cap: indirizzo.cap, raggrStruttReg: indirizzo.raggrStruttReg, comune: indirizzo.comune, localita: indirizzo.localita, codIstat: indirizzo.codIstat },
      indirizzoNormalizzato: { latitude: indirizzo.latitudine, longitude: indirizzo.longitudine }
    } : null);

    this.selectedSlot.set(calendario ? {
      data: calendario.dataRitiro,
      turno: calendario.turnoRitiro,
      turnoOreDa: calendario.turnoOraDa,
      turnoOreA: calendario.turnoOraA,
      idSlot: calendario.idSlot,
      codiceTurno: calendario.codiceTurno,
      dataLimiteAnnul: calendario.dataLimiteAnnul,
      dataLimiteModif: calendario.dataLimiteModif,
      idGiotto: calendario.idGiotto
    } : null);
    this.firstDate.set(calendario?.primaDataDisponibile ?? null);

    this.notes.set(prenotazione.infoRitiro?.note ?? '');
    this.phone.set(prenotazione.comunicazioni?.telefono ?? '');
    this.email.set(prenotazione.comunicazioni?.email ?? '');

    this.loadRemainingEditingData(prenotazione);
  }

  private loadRemainingEditingData(prenotazione: PrenotazioneViewDTO): void {
    const idServizioComunale = prenotazione.idServizioComunale;
    const codIstat = prenotazione.indirizzo?.codIstat;
    const idViario = prenotazione.cliente?.idViario ?? prenotazione.indirizzo?.idViario;
    const contratto = prenotazione.cliente?.contratto;
    const prodotti = prenotazione.prodotti ?? [];

    this.loadingService.show();
    forkJoin({
      fornitureList: idViario
        ? this.prenotazioneFOService.prenotazioneFoListUserPost({ idViario, contratto, pageSize: 999 }).pipe(
            map(response => ((response as unknown as PagedResponse)?.results ?? []) as PrenotazioneUtenteIndirizzoDto[]),
            catchError(error => {
              console.error('Error fetching fornitura for editing:', error);
              return of([] as PrenotazioneUtenteIndirizzoDto[]);
            })
          )
        : of([] as PrenotazioneUtenteIndirizzoDto[]),
      servizi: codIstat
        ? this.prenotazioneFOService.prenotazioneFoListServizioComunaleCodIstatGet(codIstat).pipe(
            catchError(error => {
              console.error('Error fetching servizio comunale list for editing:', error);
              return of([] as ServizioComunaleDto[]);
            })
          )
        : of([] as ServizioComunaleDto[]),
      servizioComunaleDetail: idServizioComunale
        ? this.prenotazioneFOService.prenotazioneFoViewServizioComunaleIdGet(idServizioComunale).pipe(
            catchError(error => {
              console.error('Error fetching servizio comunale detail for editing:', error);
              return of(null as ServiziComunaliResponse | null);
            })
          )
        : of(null as ServiziComunaliResponse | null),
      catalogoItems: idServizioComunale && prodotti.length
        ? forkJoin(prodotti.map(prodotto =>
            this.catalogoFOService.catalogoFoListProdottoGet(idServizioComunale, prodotto.codProdotto).pipe(
              map((response: CatalogoRicerca | CatalogoRicerca[]) => (Array.isArray(response) ? response : [response]).filter(Boolean)[0] ?? null),
              catchError(error => {
                console.error('Error fetching catalogo item for editing:', error);
                return of(null as CatalogoRicerca | null);
              })
            )
          ))
        : of([] as (CatalogoRicerca | null)[])
    }).pipe(
      finalize(() => this.loadingService.hide())
    ).subscribe(({ fornitureList, servizi, servizioComunaleDetail, catalogoItems }) => {
      const fornituraReale = fornitureList.flatMap(result => result.elementi ?? []).find(f => f.contratto === contratto);
      if (fornituraReale) {
        this.fornitura.set(fornituraReale);
      }

      const serviziArray = (Array.isArray(servizi) ? servizi : servizi ? [servizi] : []) as ServizioComunaleDto[];
      this.service.set(serviziArray.find(s => s.id === idServizioComunale) ?? null);

      this.sogliaMassima.set(servizioComunaleDetail?.tipoServizioComunale?.servizioGratuito?.sogliaMassima ?? null);
      this.exposureRulesText.set(servizioComunaleDetail?.note?.messaggioEsposizione ?? null);

      this.cartItems.set(
        prodotti
          .map((prodotto, index) => ({ item: catalogoItems[index], quantity: prodotto.quantita ?? 1 }))
          .filter((cartItem): cartItem is CartItem => !!cartItem.item)
      );

      // In modifica parto da step cart
      this.goToStep(3);
    });
  }

  // ==================== Invio prenotazione ====================

  private submitPrenotazione(): void {
    const payload = this.buildPrenotazionePayload();
    const isEditing = !!this.editingId();
    const request$ = isEditing
      ? this.prenotazioneFOService.prenotazioneFoUpdatePrenotazionePut(payload)
      : this.prenotazioneFOService.prenotazioneFoAddPrenotazionePost(payload);

    this.loadingService.show();
    request$.pipe(
      finalize(() => this.loadingService.hide())
    ).subscribe({
      next: (response: Prenotazione) => {
        this.submittedPrenotazione.set(response);
        const confirmRoute = isEditing ? '/richiesta-modificata' : '/richiesta-inviata';
        this.router.navigate([confirmRoute]).then(() => this.reset());
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error submitting prenotazione:', error);
        const elementName = error.error?.elementName;
        this.transitionError.set(
          elementName === 'Limite prenotazione superato'
            ? 'requestForm.steps.service.limitReached'
            : 'requestForm.confirm.submitError'
        );
      }
    });
  }

  // Costruisce il payload per /prenotazione/fo/add/prenotazione (o update, in modalità modifica)
  // dallo stato corrente del wizard.
  buildPrenotazionePayload(): Prenotazione {
    const fornitura = this.fornitura();
    const slot = this.selectedSlot();
    const indirizzoSelezionato = this.exposureAddressDetails();

    const cliente: Cliente & { idViario?: string; indirizzo?: string } = {
      codiceCliente: fornitura?.codiceCliente,
      nome: fornitura?.nome,
      cognome: fornitura?.cognome,
      contratto: fornitura?.contratto,
      fornitura: fornitura?.fornitura,
      indirizzoFornitura: fornitura?.indirizzo,
      cellulare: fornitura?.cellulare,
      telefono: this.phone(),
      email: this.email(),
      // datoFiscale prende codiceFiscale o, se non valorizzato, partitaIva
      datoFiscale: fornitura?.codiceFiscale || fornitura?.partitaIva,
      idViario: fornitura?.idViario,
      indirizzo: fornitura?.indirizzo,
      utenzaFragile: fornitura?.utenzaFragile,
      pagamenti: fornitura?.pagamenti,
      testoContratto: fornitura?.testoContratto,
      validitaDa: fornitura?.validitaDa,
      validitaA: fornitura?.validitaA,
      classe: fornitura?.classe,
      contrattoPadre: fornitura?.contrattoPadre
      // NON SETTATI: codiceTariffa/descrizioneTariffa/riduzioneCompostaggio/
      // contenitoreSecco/numeroResidenti/partitaIva
    };


    const calendario: Calendario & {
      idCalendario?: number;
      numMetriCubiDisp?: number;
      numOrdiniDisp?: number;
      tipologia?: string;
      numMaxMetriCubi?: number;
      numMaxOrdini?: number;
      dataLimitePren?: string;
      nomeCalendario?: string;
    } = slot ? {
      dataRitiro: slot.data,
      turnoRitiro: slot.turno,
      turnoOraDa: slot.turnoOreDa,
      turnoOraA: slot.turnoOreA,
      idSlot: slot.idSlot,
      codiceTurno: slot.codiceTurno,
      dataLimiteAnnul: slot.dataLimiteAnnul,
      dataLimiteModif: slot.dataLimiteModif,
      idGiotto: slot.idGiotto,
      esposizioneGiorno: slot.data,
      esposizioneOraDa: slot.turnoOreDa,
      esposizioneOraA: slot.turnoOreA,
      primaDataDisponibile: this.firstDate() ?? slot.data,
      idCalendario: slot.idCalendario,
      numMetriCubiDisp: slot.numMetriCubiDisp,
      numOrdiniDisp: slot.numOrdiniDisp,
      tipologia: slot.tipologia,
      numMaxMetriCubi: slot.numMaxMetriCubi,
      numMaxOrdini: slot.numMaxOrdini,
      dataLimitePren: slot.dataLimitePren,
      nomeCalendario: slot.nomeCalendario,
      esposizioneMessaggio: this.exposureRulesText() ?? undefined
    } : {};

    
    const geoDetails = this.exposureAddressGeoDetails();

    const indirizzo: Indirizzo = {
      via: indirizzoSelezionato?.via,
      indirizzo: indirizzoSelezionato?.indirizzo,
      civico: indirizzoSelezionato?.civico,
      barrato: indirizzoSelezionato?.barrato,
      comune: geoDetails?.indirizzo?.comune,
      idViario: indirizzoSelezionato?.idViario,
      indirizzoCompleto: indirizzoSelezionato?.indirizzoNormalizzatoCompleto ?? indirizzoSelezionato?.indirizzoCompleto,
      localita: geoDetails?.indirizzo?.localita,
      codIstat: geoDetails?.indirizzo?.codIstat,
      cap: geoDetails?.indirizzo?.cap ?? geoDetails?.indirizzoNormalizzato?.cap,
      latitudine: geoDetails?.indirizzoNormalizzato?.latitude,
      longitudine: geoDetails?.indirizzoNormalizzato?.longitude,
      raggrStruttReg: geoDetails?.indirizzo?.raggrStruttReg
    };

    const prodotti: Prodotto[] = this.cartItems().map(cartItem => ({
      codProdotto: cartItem.item.codProdotto,
      descrizione: cartItem.item.nomeProdotto,
      quantita: cartItem.quantity,
      prezzo: cartItem.item.prezzo,
      prezzoExtra: cartItem.item.prezzoExtra,
      metriCubi: cartItem.item.metriCubi,
      iva: cartItem.item.iva,
      quantitaMaxGratis: cartItem.item.quantitaMaxGratis,
      quantitaMaxPagamento: cartItem.item.quantitaMaxPagamento,
      prezzoExtraDataDa: cartItem.item.prezzoExtraDataDa,
      prezzoExtraDataA: cartItem.item.prezzoExtraDataA,
      sospensioneDataDa: cartItem.item.sospensioneDataDa,
      sospensioneDataA: cartItem.item.sospensioneDataA
      // NON SETTATI: importoIva/esenzioneIva
    }));

    const comunicazioni: Comunicazione = {
      email: this.email(),
      telefono: this.phone()
    };

    const totale: Totale = {
      isGratuito: true,
      metriCubi: this.cartVolume(),
      totalePagare: 0,
      quantitaProdotti: this.cartItems().reduce((sum, cartItem) => sum + cartItem.quantity, 0)
    };

    const infoRitiro: InfoRitiro = {
      note: this.notes()
    };

    return {
      codPrenotazione: this.editingCodPrenotazione() ?? undefined,
      idPrenotazioneOld: this.editingId() ? (this.editingId() as unknown as ObjectId) : undefined,
      idServizioComunale: this.service()?.id,
      servizioMaster: this.servizioMaster() ?? undefined,
      gestioneServizioComunale: this.gestioneServizioComunale() ?? undefined,
      cliente,
      indirizzo,
      calendario,
      prodotti,
      comunicazioni,
      totale,
      infoRitiro,
      canale: 'APP'
    };
  }

  // ==================== Persistenza stato (sessionStorage) ====================

  readonly data = computed<RequestFormData>(() => ({
    utenzaStep: {
      utenza: this.utenza()
    },
    serviceStep: {
      fornitura: this.fornitura(),
      comune: this.comune(),
      service: this.service(),
      sogliaMassima: this.sogliaMassima(),
      servizioMaster: this.servizioMaster(),
      gestioneServizioComunale: this.gestioneServizioComunale(),
      exposureRulesText: this.exposureRulesText()
    },
    cartStep: {
      cartItems: this.cartItems()
    },
    detailStep: {
      exposureAddress: this.exposureAddress(),
      exposureAddressDetails: this.exposureAddressDetails(),
      exposureAddressGeoDetails: this.exposureAddressGeoDetails(),
      selectedSlot: this.selectedSlot(),
      firstDate: this.firstDate(),
      notes: this.notes(),
      phone: this.phone(),
      email: this.email()
    }
  }));

  loadData(data: RequestFormData): void {
    this.utenza.set(data.utenzaStep.utenza);
    this.fornitura.set(data.serviceStep.fornitura);
    this.comune.set(data.serviceStep.comune);
    this.service.set(data.serviceStep.service);
    this.sogliaMassima.set(data.serviceStep.sogliaMassima);
    this.servizioMaster.set(data.serviceStep.servizioMaster);
    this.gestioneServizioComunale.set(data.serviceStep.gestioneServizioComunale);
    this.exposureRulesText.set(data.serviceStep.exposureRulesText);
    this.cartItems.set(data.cartStep.cartItems);
    this.exposureAddress.set(data.detailStep.exposureAddress);
    this.exposureAddressDetails.set(data.detailStep.exposureAddressDetails);
    this.exposureAddressGeoDetails.set(data.detailStep.exposureAddressGeoDetails);
    this.selectedSlot.set(data.detailStep.selectedSlot);
    this.firstDate.set(data.detailStep.firstDate);
    this.notes.set(data.detailStep.notes);
    this.phone.set(data.detailStep.phone);
    this.email.set(data.detailStep.email);
  }

  reset(): void {
    this.transitionError.set(null);
    this.currentStep.set(1);
    this.skipUserStep.set(false);
    this.utenza.set(null);
    this.fornitura.set(null);
    this.comune.set(undefined);
    this.service.set(null);
    this.sogliaMassima.set(null);
    this.servizioMaster.set(null);
    this.gestioneServizioComunale.set(null);
    this.exposureRulesText.set(null);
    this.cartItems.set([]);
    this.exposureAddress.set(null);
    this.exposureAddressDetails.set(null);
    this.exposureAddressGeoDetails.set(null);
    this.selectedSlot.set(null);
    this.firstDate.set(null);
    this.notes.set('');
    this.phone.set('');
    this.email.set('');
    this.detailFormValid.set(false);
    this.editingId.set(null);
    this.editingCodPrenotazione.set(null);
    sessionStorage.removeItem(STORAGE_KEY);
  }

  private persist(): void {
    const state: RequestFormState = {
      currentStep: this.currentStep(),
      skipUserStep: this.skipUserStep(),
      data: this.data()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  private restore(): void {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }

    try {
      const state: RequestFormState = JSON.parse(raw);
      this.currentStep.set(state.currentStep);
      this.skipUserStep.set(state.skipUserStep);
      this.loadData(state.data);
    } catch (error) {
      console.error('Error restoring request form state:', error);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
}
